import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { catchError, EMPTY, filter, finalize, switchMap } from 'rxjs';
import { openConfirmDialog } from '@/common/confirm-dialog/confirm-dialog.component';
import { InfoboxService } from '@/common/infobox/infobox.service';
import type {
	ContactDataInterface,
	OrganizationContact,
	OrganizationContactDepartment,
	OrganizationContactDialogData,
} from '@/organization/organization/organization.model';
import { OrganizationContactDialogComponent } from './organization-contact-dialog/organization-contact-dialog.component';
import { OrganizationContactsApiService } from './organization-contacts.api.service';

interface OrganizationContactRow {
	contact: OrganizationContact;
	fullName: string;
	departmentName: string | null;
}

@Component({
	selector: 'app-organization-contacts',
	standalone: true,
	imports: [MatButtonModule, MatTooltipModule, RouterLink],
	templateUrl: './organization-contacts.component.html',
	styleUrl: './organization-contacts.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationContactsComponent implements OnInit {
	readonly organizationId = input.required<number>();
	readonly departments = input<ContactDataInterface[] | null>([]);

	readonly contacts = signal<OrganizationContact[]>([]);
	readonly isLoading = signal(true);
	readonly loadFailed = signal(false);
	readonly deletingId = signal<number | null>(null);
	readonly departmentOptions = computed<OrganizationContactDepartment[]>(() =>
		(this.departments() ?? [])
			.filter((department) => department.id != null)
			.map((department) => ({ id: department.id, name: department.kind?.name ?? `Dział #${department.id}` })),
	);
	readonly rows = computed<OrganizationContactRow[]>(() => {
		const names = new Map(this.departmentOptions().map((department) => [department.id, department.name]));

		return this.contacts().map((contact) => ({
			contact,
			fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—',
			departmentName:
				contact.department === null ? null : (names.get(contact.department) ?? `Dział #${contact.department}`),
		}));
	});

	private readonly apiService = inject(OrganizationContactsApiService);
	private readonly dialog = inject(MatDialog);
	private readonly infobox = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.load();
	}

	load(): void {
		this.isLoading.set(true);
		this.loadFailed.set(false);

		this.apiService
			.getContacts(this.organizationId())
			.pipe(
				catchError(() => {
					this.loadFailed.set(true);
					return EMPTY;
				}),
				finalize(() => this.isLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((contacts) => this.contacts.set(contacts));
	}

	remove(row: OrganizationContactRow): void {
		const contactId = row.contact.id;

		openConfirmDialog(this.dialog, {
			title: 'Usunąć osobę kontaktową?',
			message: `Osoba „${row.fullName}” zostanie trwale usunięta z organizacji.`,
			confirmLabel: 'Usuń',
			danger: true,
		})
			.afterClosed()
			.pipe(
				filter(Boolean),
				switchMap(() => {
					this.deletingId.set(contactId);

					return this.apiService.deleteContact(this.organizationId(), contactId).pipe(
						catchError(() => EMPTY),
						finalize(() => this.deletingId.set(null)),
					);
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.contacts.update((contacts) => contacts.filter((contact) => contact.id !== contactId));
				this.infobox.success('Usunięto osobę kontaktową');
			});
	}

	openDialog(contact: OrganizationContact | null): void {
		this.dialog
			.open<OrganizationContactDialogComponent, OrganizationContactDialogData, OrganizationContact>(
				OrganizationContactDialogComponent,
				{
					data: { organizationId: this.organizationId(), contact, departments: this.departmentOptions() },
					width: '560px',
					maxWidth: '96vw',
				},
			)
			.afterClosed()
			.pipe(
				filter((saved): saved is OrganizationContact => !!saved),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((saved) => {
				this.contacts.update((contacts) =>
					contacts.some((item) => item.id === saved.id)
						? contacts.map((item) => (item.id === saved.id ? saved : item))
						: [...contacts, saved],
				);
				this.infobox.success(contact ? 'Zapisano osobę kontaktową' : 'Dodano osobę kontaktową');
			});
	}
}
